import {
  For,
  Show,
  createEffect,
  createMemo,
  createSignal,
  onMount,
} from "solid-js";

import AffiliateClaiTip from "@/components/Base/AffiliateClaiTip";
import Icon, { svgPathTypes } from "@/components/Icon";
import { LocalStorageKey } from "@/constants/constants";
import { useGame } from "@/hooks/game/useGame.ts";
import { useRouter } from "@/hooks/useRouter.tsx";
import { t } from "@/i18n";
import { isMobile } from "@/lib/isMobile";
import { getLocalStorageItem } from "@/lib/localStorage";
import { sdkManager } from "@/lib/sdk/sdkManager";
import { removeLocalePrefix } from "@/lib/urlUtils.ts";
import { sysConfigStore } from "@/stores/sysConfigStore";
import { userStore } from "@/stores/userStore";
import { useLocation } from "@solidjs/router";

type NavItem = {
  label: string;
  icon: svgPathTypes;
  path: string;
  needLogin?: boolean;
  showRedDot?: boolean;
  report?: string;
};

export default function BottomNav() {
  const [i18nReady, setI18nReady] = createSignal(false);

  onMount(() => {
    (async () => {
      setI18nReady(true);
    })();
  });

  const defaultSportGameId = createMemo(() => {
    if (!sysConfigStore.initReady) {
      return 0;
    }
    return sysConfigStore.sport_config.sport_game_id;
  });

  const navItems: NavItem[] = [
    {
      label: t("home.title"),
      icon: "bottom-nav/menu",
      path: "/",
      report: "menu_nav_click",
    },
    {
      label: t("home.sports"),
      icon: "bottom-nav/sport",
      path: "/sports",
      report: "sports_nav_click",
    },
    {
      label: t("home.deposit"),
      icon: "bottom-nav/deposit",
      path: "/deposit",
      needLogin: true,
      report: "deposit_nav_click",
    },
    {
      label: t("home.affiliate"),
      icon: "bottom-nav/affiliate",
      path: "/affiliate",
      showRedDot: userStore.affiliateRewardTip,
      report: "affiliate_nav_click",
    },
    {
      label: t("home.account"),
      icon: "bottom-nav/account",
      path: "/profile",
      report: "my_nav_click",
    },
  ];

  const { push, replace, toDeposit } = useRouter();
  console.log(`BottomNav location.pathname`);
  if (!isMobile()) replace("/");

  const { enterGame } = useGame();
  const location = useLocation();

  const isActive = (path: string) => {
    const currentPath = removeLocalePrefix(location.pathname) || "";
    if (["/sports", "/affiliate"].includes(path)) {
      return currentPath.startsWith(path);
    }
    return currentPath === path;
  };

  const handleNoLogin = () => {
    const lastLoginTime = getLocalStorageItem(LocalStorageKey.LastLoginTime, 0);
    push(lastLoginTime ? "/login" : "/register");
  };

  return (
    <Show when={i18nReady()} fallback={<></>}>
      <div class="prevent-zoom box-border flex h-full w-full items-center justify-between rounded-t-2xl border-t-[1px] border-[var(--colors-border-card-border2)] bg-[var(--colors-surface-bg-9)] py-[10px] backdrop-blur-[50px]">
        <For each={navItems}>
          {(item) => (
            <div
              class="prevent-zoom relative flex h-[45px] flex-1 cursor-pointer flex-col items-center gap-[2px] text-[var(--colors-text-tertiary)]"
              classList={{
                "text-white": location.pathname.includes(item.path),
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (item.report) {
                  sdkManager.tdReportEvent(item.report, {
                    source: window.location.pathname,
                  });
                }
                if (item.needLogin && !userStore.isLogin) {
                  handleNoLogin();
                  return;
                }

                if (item.path === "/sports") {
                  enterGame(defaultSportGameId());
                } else if (item.path === "/deposit") {
                  toDeposit();
                } else {
                  push(item.path);
                }
              }}
            >
              <Show when={item.showRedDot} fallback={<></>}>
                <div class="pointer-events-none absolute inset-0">
                  <AffiliateClaiTip class="right-[15px] -translate-x-[5px]" />
                </div>
              </Show>

              <Icon
                name={item.icon as svgPathTypes}
                class={`h-6 w-6 ${
                  isActive(item.path)
                    ? "text-[var(--colors-icon-green-brand)]"
                    : "text-[var(--colors-icon-tab-default)]"
                }`}
              />
              <div class="relative flex items-center gap-[6px]">
                <Show when={isActive(item.path)}>
                  <div class="h-1 w-1 rounded-full bg-[var(--colors-icon-green-sed)]" />
                </Show>
                <span class="font-['Alexandria'] text-[13px] font-semibold leading-[13px]">
                  {item.label}
                </span>
              </div>
            </div>
          )}
        </For>
      </div>
    </Show>
  );
}
